// @ts-nocheck
import React, { useState, useEffect, useMemo } from "react";
import { Chart as ChartJS, registerables } from "chart.js";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";
import ProcessedScatterGraph from "../forecastGraphs/ProcessedScatterGraph";
import { SkeletonChart } from "../Skeleton";
import ForecastAccuracySummary from "./ForecastAccuracySummary";
import ForecastIssuePicker from "./ForecastIssuePicker";
import {
  formatProcessedDataGraph,
  separatePercentileChannels,
} from "../../util/utils";
import { fetchAndParseForecastRecord } from "../../utils/modelOutputUtils";
import { readSwatch } from "../../utils/colorUtils";
import {
  customViewModelColor,
  fitPreviewWindow,
  listForecastIssues,
  loadIssueAccuracy,
  previewLegend,
  refetchRangeLines,
  timeSpan,
} from "../../utils/composedForecastUtils";
import type { CombinedPreviewData, ForecastIssue, IssueAccuracy } from "../../utils/composedForecastUtils";
import { ACCURACY_METRICS } from "../../utils/forecastAccuracy";
import { applyChannelDisplay, DEFAULT_ERROR_COLOR } from "../../utils/composedForecastDisplay";
import { weightModelData } from "../../utils/composedForecastWeight";

ChartJS.register(...registerables);

interface ComposedForecastCombinedChartProps {
  baseModel: CombinedPreviewData["baseModel"];
  overlayLines: CombinedPreviewData["overlayLines"];
  /** Observation source + scoring settings; when set, the base model is scored per forecast issue. */
  accuracy?: CombinedPreviewData["accuracy"];
  // When true, the chart fills its parent (h-full w-full) instead of using the built-in fixed heights. Use this on the asset dashboard where the parent is a flex container that should drive the size. Default false preserves the form-modal preview's fixed layout.
  fillContainer?: boolean;
  // The view's saved sources: given, a legend above the chart lists every one with its status; omitted where the parent has no room (the asset graph)
  legendSources?: any[];
  // Stacks the forecast-issue picker under a full-width legend instead of beside it (the Custom View modal)
  pickerBelowLegend?: boolean;
}

const ComposedForecastCombinedChart: React.FC<
  ComposedForecastCombinedChartProps
> = ({
  baseModel,
  overlayLines,
  accuracy = null,
  fillContainer = false,
  legendSources,
  pickerBelowLegend = false,
}) => {
  const [loading, setLoading] = useState(!!baseModel);
  const [error, setError] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<{
    percentiles: any;
    otherChannels: any;
    yUnit: string;
    title: string;
    modelData: any;
  } | null>(null);

  // Forecast issues of the base model, newest first; index 0 is the latest (baseModel.s3Key)
  const [issues, setIssues] = useState<ForecastIssue[]>([]);
  const [issueIndex, setIssueIndex] = useState(0);
  const [scored, setScored] = useState<IssueAccuracy | null>(null);
  const [scoring, setScoring] = useState(false);
  const [scoreError, setScoreError] = useState<string | null>(null);

  useEffect(() => {
    setIssues([]);
    setIssueIndex(0);
    if (!baseModel?.outputId) return;
    let cancelled = false;
    listForecastIssues(baseModel.outputId)
      .then((list) => {
        if (!cancelled) setIssues(list);
      })
      .catch((err) => console.error("Failed to list forecast issues:", err));
    return () => {
      cancelled = true;
    };
  }, [baseModel?.outputId]);

  const s3Key = issues[issueIndex]?.s3Key ?? baseModel?.s3Key;

  // Load base model data for the selected issue
  useEffect(() => {
    if (!baseModel || !s3Key) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { parsedData, defaultModelKey } = await fetchAndParseForecastRecord(s3Key);
        if (cancelled) return;

        const models = parsedData?.models || {};
        const modelData = models[defaultModelKey];
        if (!modelData?.data) {
          setError("No data in forecast file");
          return;
        }

        // The source's weight multiplies the bands, the mean and what the accuracy score compares
        const weightedData = weightModelData(modelData.data, baseModel.weight);
        const formatted = formatProcessedDataGraph(weightedData);
        const { otherChannels, percentiles } =
          separatePercentileChannels(formatted);
        setGraphData({
          percentiles,
          otherChannels,
          yUnit: modelData.yUnit || "",
          title: baseModel.sourceName,
          modelData: weightedData,
        });
      } catch (err: any) {
        if (!cancelled) setError(err.message || "Failed to load base model");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [s3Key, baseModel?.weight]);

  // Score the selected issue against the observation over the issue's own window
  useEffect(() => {
    setScored(null);
    setScoreError(null);
    if (!accuracy || !graphData?.modelData) return;
    let cancelled = false;
    setScoring(true);
    loadIssueAccuracy(accuracy, graphData.modelData)
      .then((res) => {
        if (!cancelled) setScored(res);
      })
      .catch((err) => {
        if (!cancelled) setScoreError(err?.message || "Failed to load observations");
      })
      .finally(() => {
        if (!cancelled) setScoring(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accuracy, graphData]);

  // The base model's display settings: hidden channels dropped, chosen colours passed through (memoised so the
  // memo'd ProcessedScatterGraph isn't handed new objects every render)
  const shown = useMemo(
    () =>
      graphData ? applyChannelDisplay(graphData.percentiles, graphData.otherChannels, baseModel?.display) : null,
    [graphData, baseModel?.display]
  );

  // The base model's own time span, once loaded, anchors the window every overlay is clipped to
  const modelRange = useMemo(() => {
    if (!graphData) return null;
    const times = [
      ...Object.values(graphData.percentiles || {}),
      ...Object.values(graphData.otherChannels || {}),
    ]
      .flat()
      .map((p: any) => new Date(p?.x).getTime())
      .filter(Number.isFinite);
    return timeSpan(times);
  }, [graphData]);

  const showLegend = !!legendSources?.length;

  // Range lines (a historical model's series, a curated dataset) were read for their newest data; once the base
  // issue's span is known they are read again for that window (stage 5, decision 2), and again when the issue changes
  const [rangeLines, setRangeLines] = useState<Map<string, (typeof overlayLines)[number]>>(new Map());
  useEffect(() => {
    if (!baseModel || !modelRange || !overlayLines.some((line) => line.rangeRef)) {
      setRangeLines(new Map());
      return;
    }
    // The previous window's lines stay up until the new ones land, so an issue change never flashes the unwindowed read
    let cancelled = false;
    refetchRangeLines(overlayLines, modelRange)
      .then((lines) => {
        if (cancelled) return;
        setRangeLines(new Map(lines.filter((line) => line.rangeRef).map((line) => [line.sourceId, line])));
      })
      .catch((err) => console.error("Failed to re-read range sources for the forecast's window:", err));
    return () => {
      cancelled = true;
    };
  }, [overlayLines, baseModel, modelRange]);
  const windowedLines = useMemo(
    () => (rangeLines.size ? overlayLines.map((line) => rangeLines.get(line.sourceId) ?? line) : overlayLines),
    [overlayLines, rangeLines]
  );

  const fitted = useMemo(
    () => fitPreviewWindow(windowedLines, baseModel ? modelRange : null),
    [windowedLines, baseModel, modelRange]
  );

  // Clipped overlays; when scored, the observation follows the selected issue's window and the error series goes on the right axis
  const chartOverlays = useMemo(() => {
    if (!accuracy || !scored) return fitted.overlayLines;
    // The observation is redrawn from the scored window, so it comes back even if its stale records were clipped out
    const clippedOut = windowedLines.filter(
      (line) => line.isObservation && !fitted.overlayLines.some((f) => f.name === line.name)
    );
    const lines = [...fitted.overlayLines, ...clippedOut].map((line) =>
      line.isObservation
        ? { ...line, records: scored.observations.map((o) => ({ timestamp: new Date(o.t), value: o.value })) }
        : line
    );
    const plotDef = ACCURACY_METRICS.find((m) => m.id === accuracy.config.plotMetric);
    if (plotDef && scored.result.series.length) {
      const unit = plotDef.unit === "unit" ? graphData?.yUnit : plotDef.unit === "unit²" ? `(${graphData?.yUnit})²` : plotDef.unit;
      const axisLabel = unit ? `${plotDef.label} (${unit})` : plotDef.label;
      lines.push({
        name: plotDef.label,
        records: scored.result.series.map((p) => ({ timestamp: new Date(p.t), value: p.value })),
        color: accuracy.errorColor || DEFAULT_ERROR_COLOR,
        axis: "right",
        axisLabel,
        // Same unit as the forecast (error, absolute error): share the left axis's scale so they read alike
        sharedScale: plotDef.unit === "unit",
      });
    }
    return lines;
  }, [fitted, windowedLines, accuracy, scored, graphData?.yUnit]);

  // Fallback chart for when there's no base processed model
  const fallbackChartData = useMemo(() => {
    if (baseModel || fitted.overlayLines.length === 0) return null;

    return {
      datasets: fitted.overlayLines.map((line) => ({
        label: line.name,
        data: line.records.map((r) => ({
          x: new Date(r.timestamp),
          y: r.value,
        })),
        borderColor: line.color,
        backgroundColor: line.color,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 3,
        tension: 0.2,
        fill: false,
      })),
    };
  }, [baseModel, fitted]);

  const fallbackOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        // The HTML legend replaces Chart.js's when shown, so the two never double up
        legend: {
          display: !showLegend,
          position: "top" as const,
          labels: { boxWidth: 12, font: { size: 11 } },
        },
        tooltip: {
          callbacks: {
            title: (items: any) =>
              items[0] ? new Date(items[0].parsed.x).toLocaleString() : "",
          },
        },
      },
      scales: {
        x: {
          type: "time" as const,
          grid: { display: false },
          ticks: { font: { size: 10 }, maxTicksLimit: 8 },
        },
        y: {
          grid: { color: "rgba(0,0,0,0.05)" },
          ticks: { font: { size: 10 } },
        },
      },
    }),
    [showLegend]
  );

  const legendItems = showLegend
    ? previewLegend(legendSources, {
        loadedLines: windowedLines,
        // What the chart actually draws, so a scored observation brought back into the window isn't listed as missing
        fit: {
          overlayLines: chartOverlays,
          outside: fitted.outside.filter((o) => !chartOverlays.some((line) => line.sourceId === o.sourceId)),
        },
        baseModelDrawn: !!(graphData && baseModel),
        baseModelBanded: !!graphData && Object.keys(graphData.percentiles || {}).length > 0,
      })
    : [];

  // Colour + name, plus "daily averages" when downsampled; the type ("Model output · shaded range") is in the hover title
  const showPicker = issues.length > 1;
  const legend = showLegend && legendItems.length > 0 && (
    <div className={`flex min-w-0 flex-1 flex-wrap gap-x-4 gap-y-1.5 ${showPicker && !pickerBelowLegend ? "sm:justify-end" : ""} font-inter text-eai-body-xs`}>
      {legendItems.map((item) => (
        <span key={item.id} className="flex min-w-0 items-center gap-1.5" title={item.note ? `${item.name} — ${item.note}` : item.name}>
          <span
            aria-hidden
            className={`h-2 w-2 shrink-0 rounded-full ${item.dim ? "opacity-40" : ""}`}
            style={{ backgroundColor: item.color }}
          />
          <span className={item.dim ? "text-eai-ink-faint" : "text-eai-ink"}>{item.name}</span>
          {item.averaged && <span className="whitespace-nowrap text-eai-ink-faint">· {item.averaged}</span>}
        </span>
      ))}
    </div>
  );

  // Picker first (top on phones, top-left on desktop) with the legend right-aligned beside it; or stacked under the legend
  const controlRow = (picker?: React.ReactNode) =>
    (legend || picker) && (
      <div className={`mb-4 flex shrink-0 flex-col gap-2 ${pickerBelowLegend ? "" : "sm:flex-row sm:items-start sm:gap-4"}`}>
        {legend}
        {picker && <div className={`w-full shrink-0 sm:w-[320px] ${pickerBelowLegend ? "" : "order-first"}`}>{picker}</div>}
      </div>
    );

  if (loading && !graphData) {
    return <SkeletonChart bars={16} label="Loading combined preview" withHeader={false} />;
  }

  // The legend still lists every source when the chart can't draw, so the view's makeup never disappears
  // Every case shares this frame: with fillContainer it's the flex column the chart's flex-1 grows in. Case 2 and the
  // messages used to return a bare fragment, so the chart sat at its min height in the asset dashboard.
  const frameClass = fillContainer ? "flex h-full w-full flex-col" : "w-full";

  if (error) {
    return (
      <div className={frameClass}>
        {controlRow()}
        <div className="py-6 text-center font-inter text-eai-body-sm text-eai-danger-text">
          {error}
        </div>
      </div>
    );
  }

  // Case 1: Base model exists → ProcessedScatterGraph with overlay lines
  if (graphData && baseModel && shown) {
    const chart = (
      <ProcessedScatterGraph
        key={s3Key}
        percentiles={shown.percentiles}
        otherChannels={shown.otherChannels}
        channelColors={shown.channelColors}
        bandColors={shown.bandColors}
        yaxis={graphData.yUnit || "Value"}
        timeZone={"UTC"}
        assetTimeZone={"UTC"}
        timeZoneMode={"utc"}
        title={graphData.title}
        additionalDataSources={chartOverlays.length > 0 ? chartOverlays : null}
        hideGraphControls
        hideMobileControls={false}
        meanColor={customViewModelColor()}
        meanLabel={baseModel.sourceName}
        nowLineColor={readSwatch("text-eai-ink-faint")}
      />
    );

    return (
      <div className={frameClass}>
        {controlRow(showPicker && <ForecastIssuePicker issues={issues} index={issueIndex} onChange={setIssueIndex} />)}
        <div className={fillContainer ? "h-[260px] sm:h-auto sm:min-h-[240px] sm:flex-1" : "h-[280px] w-full sm:h-[370px]"}>{chart}</div>
        {accuracy && (
          <ForecastAccuracySummary
            config={accuracy.config}
            result={scored?.result ?? null}
            observationName={accuracy.sourceName}
            observationIssue={scored?.observationIssue}
            unit={graphData.yUnit}
            loading={scoring || loading}
            error={scoreError}
          />
        )}
      </div>
    );
  }

  // Case 2: No base model → multi-line Chart.js
  if (fallbackChartData) {
    return (
      <div className={frameClass}>
        {controlRow()}
        <div className={fillContainer ? "h-[260px] w-full sm:h-auto sm:min-h-[240px] sm:flex-1" : "h-[300px] w-full"}>
          <Line data={fallbackChartData} options={fallbackOptions} />
        </div>
      </div>
    );
  }

  return (
    <div className={frameClass}>
      {controlRow()}
      <div className="py-6 text-center font-inter text-eai-body-sm text-eai-ink-faint">
        No previewable data sources
      </div>
    </div>
  );
};

export default ComposedForecastCombinedChart;
